import { useRecoilState } from 'recoil';
import { QuoteProductItem, SelectedQuoteItem } from '../../interfaces';
import { SelectedSubscriptionPlan } from '../../interfaces/QuoteItem.types';
import {
  ProductDetail,
  useQuoteProductInformation,
} from '../../hooks/use-quote-product-information';
import { useAddLineItems } from '../../hooks/use-add-quote-line-items';
import {
  DEFAULT_QUOTE_INFO,
  generateProductInformationPayload,
  generateProductLines,
  generateQuoteLinesPayload,
  QuoteInformationAtom,
  quoteInformationAtom,
  getCMPToken,
} from '../internal';
import { useLocation } from 'react-router-dom';
import { useCallback, useEffect } from 'react';
import { useSiteConfig } from 'mf_core/config';

export const QUOTE_EDITOR_URL = 'my/quotemgmt/quoteitems';

export const useAddToQuote = () => {
  const { siteCode } = useSiteConfig();
  const location = useLocation();
  const { isPending, mutateAsync: getProductInformation } =
    useQuoteProductInformation();
  const { mutateAsync: addLineItems } = useAddLineItems();

  const [quoteInfo, setQuoteInfo] = useRecoilState(quoteInformationAtom);

  const showQuoteButton = Boolean(quoteInfo.quoteNumber);

  const setGlobalQuoteInfo = (quoteInfo: QuoteInformationAtom) => {
    setQuoteInfo(quoteInfo);
  };

  const addTSProducts = async (products: QuoteProductItem[]) => {
    const _products = [...products];
    const productItems = generateProductInformationPayload(
      _products,
      quoteInfo.branchNumber,
      siteCode
    );
    if (productItems.length === 0) return;

    let productInfoResponse = await getProductInformation(productItems);
    if (
      productInfoResponse?.validSkus &&
      productInfoResponse?.validSkus?.length > 0
    ) {
      _products.forEach((product) => {
        const match = productInfoResponse?.validSkus?.find(
          (skuItem) =>
            skuItem.vendorPartNumber.trim() === product.skuVpnNumber.trim() ||
            skuItem?.productDetails?.[0]?.ingramPartNumber ===
              product.skuVpnNumber.trim()
        );
        const productDetails =
          match && match.productDetails?.[0]
            ? match.productDetails?.[0]
            : ({} as ProductDetail);
        if (
          productDetails?.bundle?.length > 0 &&
          productDetails?.bundleSkus?.length > 0
        ) {
          const { bundle, bundleSkus } = productDetails;
          product.bundle = bundle;
          product.bundleSkus = bundleSkus;
        }
      });
      const hasBundleSkus = _products.some(
        (item) =>
          item?.bundle &&
          item?.bundle?.length > 0 &&
          item?.bundleSkus &&
          item?.bundleSkus?.length > 0
      );
      if (hasBundleSkus) {
        const productItemsForBundles = generateProductInformationPayload(
          _products,
          quoteInfo.branchNumber,
          siteCode
        );
        productInfoResponse = await getProductInformation(
          productItemsForBundles
        );
      }
    }
    const newQuoteLines = generateProductLines(
      _products,
      productInfoResponse?.validSkus,
      siteCode
    );

    if (newQuoteLines && newQuoteLines.length > 0) {
      const quoteLinesPayload = generateQuoteLinesPayload(
        newQuoteLines,
        'Addtoquote',
        quoteInfo
      );
      const response = await addLineItems(quoteLinesPayload);
      setQuoteInfo({
        ...quoteInfo,
        quoteLinesItems: response?.quoteLines ?? [],
      });
      return response;
    }
    return null;
  };

  const addCloudProducts = async (subscription: SelectedSubscriptionPlan) => {
    let quoteLinesPayload: any;
    const newPlanOptions: any[] = [];
    let updatedPlanOptions: any[] = [];
    subscription.subscriptionOptions?.forEach((option) => {
      const planOptionsExists: any =
        quoteInfo.quoteLinesItems?.find(
          (line: any) => line?.vpn && line?.vpn === option.vpn
        ) || {};
      if (planOptionsExists && Object.keys(planOptionsExists).length > 0) {
        updatedPlanOptions.push({
          ...planOptionsExists,
          quantity:
            (Number(planOptionsExists?.quantity) || 0) +
            (Number(option.quantity) || 0),
        });
      } else {
        newPlanOptions.push(option);
      }
    });

    if (newPlanOptions?.length > 0) {
      quoteLinesPayload = generateQuoteLinesPayload(
        newPlanOptions,
        'Addtoquote',
        quoteInfo
      );
      const response = await addLineItems({
        ...quoteLinesPayload,
        cmptoken: getCMPToken(),
      });
      setQuoteInfo({
        ...quoteInfo,
        quoteLinesItems: response?.quoteLines ?? [],
      });
      updatedPlanOptions = [];
    }
    if (updatedPlanOptions?.length > 0) {
      const updatedSubscriptionOptions = updatedPlanOptions.map((option) => {
        return {
          duplicateselected: true,
          lineId: option?.lineNumber,
          markUp: option?.markUp,
          markUpUnit: option?.markupUnit,
          quantity: option?.quantity.toString(),
          sequenceNumber: option?.sequenceNumber,
          skuvpn: option?.pnaResponse?.ingramPartNumber,
          vendorNumber:
            option?.pnaResponse?.productAttributes?.[0]?.vendorNumber,
          vpn: option?.panResponse?.vendorPartNumber,
        };
      });

      if (updatedSubscriptionOptions?.length > 0) {
        quoteLinesPayload = generateQuoteLinesPayload(
          updatedSubscriptionOptions,
          'Updatequantity',
          quoteInfo
        );
        const response = await addLineItems({
          ...quoteLinesPayload,
          cmptoken: getCMPToken(),
        });
        setQuoteInfo({
          ...quoteInfo,
          quoteLinesItems: response?.quoteLines ?? [],
        });
      }
    }
    return null;
  };

  const addToQuote = async (
    productType: keyof SelectedQuoteItem,
    products?: QuoteProductItem[] | null,
    subscription?: any
  ) => {
    if (productType === 'product') {
      const response = await addTSProducts(products as QuoteProductItem[]);
      return response;
    }
    if (productType === 'subscription') {
      const response = await addCloudProducts(
        subscription as SelectedSubscriptionPlan
      );
      return response;
    }
  };

  const clearQuote = useCallback(
    () => setQuoteInfo(DEFAULT_QUOTE_INFO),
    [setQuoteInfo]
  );

  useEffect(() => {
    if (showQuoteButton) {
      const path = location.pathname.split('/');
      if (!(path.includes('product') || path.includes('quotemgmt'))) {
        clearQuote();
      }
    }
  }, [clearQuote, location, showQuoteButton]);

  return {
    addToQuote,
    clearQuote,
    isLoading: isPending,
    quoteEditorURL: `/${QUOTE_EDITOR_URL}?quoteId=${quoteInfo.quoteNumber}&quoteGuid=${quoteInfo.quoteGuid}&quoteType=QMDraft`,
    quoteInfo,
    quoteItemsCount: quoteInfo.quoteLinesItems?.length ?? 0,
    setGlobalQuoteInfo,
    showQuoteButton,
  };
};
